/** @jsxImportSource @emotion/react */
import { cloneElement, useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import useNotifications from '../../shared/hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../login/v1/utility';
import { aiBotUpgradeModalOpenAtom } from './state';
import { openPopup } from '../../shared/utils/popup';
import QSModal from '../../shared/components/molecules/modal';
import { css } from '@emotion/react';
import { QSButton, QSIconButton, QSTypography } from '../../shared/components/atoms';
import ProgressiveImage from '../../shared/components/atoms/progressive-image';
import { Close } from '@mui/icons-material';
import AIAgent from './images/ai_agent.webp';
import AIAnalytics from './images/ai_analytics.png';
import AIAwaitingReplies from './images/awaiting_replies.png';
import AIGenerateReplySuggestion from './images/generate_reply_suggestions.webp';
import AIRephraseText from './images/rephrase_text.png';
import AISummarizeChatsAndCalls from './images/summarize_chats_and_calls.webp';
import BotIcon from '../../shared/icons/icons-revamp/bot';
import AIMessageIcon from '../../shared/icons/icons-revamp/ai_message';
import AISummaryIcon from '../../shared/icons/icons-revamp/ai_summary';
import AIAnalyticsIcon from '../../shared/icons/icons-revamp/ai_analytics';
import AIScanIcon from '../../shared/icons/icons-revamp/ai_scan';
import AISuggestionIcon from '../../shared/icons/icons-revamp/ai_suggestions';
import { userProfileAtom } from '../../shared/states/user';
import { MAIN_IN_SUPPORT_NUMBER } from '../../shared/constants';

const AIBlockerThumbnails = [
  AIAgent,
  AIRephraseText,
  AISummarizeChatsAndCalls,
  AIAnalytics,
  AIAwaitingReplies,
  AIGenerateReplySuggestion,
];
const FEATURES = [
  {
    id: 0,
    icon: <BotIcon size={24} color="#7A7A7A" />,
    title: 'AI Chat Agents',
    description: 'ChatGPT powered AI agents trained on your data',
  },
  {
    id: 1,
    icon: <AIMessageIcon size={24} />,
    title: 'Fix spelling and grammar',
    description: 'Automatically correct any typing mistakes in message',
  },
  {
    id: 2,
    icon: <AISummaryIcon size={24} />,
    title: 'Summarise Chats and Calls',
    description: 'Full context in seconds without going through the entire conversation',
  },
  {
    id: 3,
    icon: <AIAnalyticsIcon size={24} />,
    title: 'AI Powered Analytics',
    description: 'Find the voice of the customer with AI custom fields',
  },
  {
    id: 4,
    icon: <AIScanIcon size={24} />,
    title: 'AI scan for “Awaiting Reply”',
    description: 'Skip chats with closing responses like “okay”, “thank you”',
  },
  {
    id: 5,
    icon: <AISuggestionIcon size={24} />,
    title: 'Generate reply suggestions',
    description: 'Let your AI agent suggest contextual replies in chat',
  },
];
const conicGradient =
  'conic-gradient(from 180deg at 50% 50%, #B85BF6 57.59999871253967deg, #9154F6 129.6000051498413deg, #C755F9 233.99999141693115deg, #9871F8 360deg)';

export default function AIBotUpgradeModal() {
  const [upgradeModal, setUpgradeModal] = useAtom(aiBotUpgradeModalOpenAtom);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const userProfile = useAtomValue(userProfileAtom);
  let contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=I%27m%20interested%20in%20buying%20AI%20Bot%20Addon`;
  const initialStep = typeof upgradeModal.step === 'number' ? upgradeModal.step : 0;
  const [iconNum, setIconNum] = useState<number>(initialStep);

  useEffect(() => {
    if (upgradeModal.open) {
      setIconNum(upgradeModal.step ?? 0);
    }
  }, [upgradeModal.open, upgradeModal.step]);

  const handleFeatureSelect = useCallback((id: number) => {
    setIconNum(id);
  }, []);

  const onClose = useCallback(() => {
    setUpgradeModal({ open: false, step: 0 });
  }, [setUpgradeModal]);

  const onClickContactUs = useCallback(async () => {
    try {
      if (hideDTDetails) {
        openPopup(contactUsLink, '_blank');
      } else {
        const salesPhone = !userProfile
          ? MAIN_IN_SUPPORT_NUMBER
          : userProfile.countryCode === 'IN'
          ? MAIN_IN_SUPPORT_NUMBER
          : '971588853933';
        const encodedMessage = encodeURIComponent(
          "Hi! I am interested in unlocking DoubleTick's AI features to 10x my team's productivity."
        );
        const link = `https://wa.me/${salesPhone}?text=${encodedMessage}`;
        window.open(link, ' _blank');
      }
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  if (!upgradeModal.open) {
    return null;
  }

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal.open}
      onClose={onClose}
    >
      <div
        css={css`
          display: flex;
          flex-shrink: 0;
          border-radius: 8px;
          width: 900px;
          height: 643px;
          background: white;
          outline: none;
          position: relative;
          min-width: 704px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>

        <div
          css={css`
            display: flex;
            padding: 24px;
            flex-direction: column;
            align-items: flex-start;
            justify-content: space-between;
            flex: 1 0 0;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                align-self: stretch;
              `}
            >
              <div
                css={css`
                  display: flex;
                  height: 24px;
                  padding: 4px 6px;
                  justify-content: center;
                  align-items: flex-start;
                  gap: 8px;
                  border-radius: 6px;
                  background: rgba(137, 89, 229, 0.08);
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                  `}
                >
                  <BotIcon
                    width={16}
                    height={16}
                    color="#8959E5"
                    css={css`
                      background: #8959e5;
                      -webkit-background-clip: text;
                      background-clip: text;
                      color: transparent;
                    `}
                  />
                  <span
                    css={css`
                      font-family: Inter;
                      font-size: 12px;
                      font-style: normal;
                      font-weight: 600;
                      line-height: 140%;
                      background: #8959e5;
                      background-clip: text;
                      -webkit-background-clip: text;
                      -webkit-text-fill-color: transparent;
                    `}
                  >
                    Unlock AI Features
                  </span>
                </div>
              </div>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 8px;
                  align-self: stretch;
                `}
              >
                <QSTypography
                  css={css`
                    align-self: stretch;
                    color: var(--Neutral-Primary, #2d2d2d);
                    font-family: Inter;
                    font-size: 22px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 160%;
                  `}
                >
                  Unlock productivity and reduce your team’s workload with our AI features
                </QSTypography>
                <QSTypography
                  css={css`
                    align-self: stretch;
                    color: #7a7a7a;
                    font-family: Inter;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 160%;
                  `}
                >
                  All our AI features in one package
                </QSTypography>
              </div>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 8px;
                  align-self: stretch;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 4px;
                    align-self: stretch;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      gap: 4px;
                      align-self: stretch;
                    `}
                  >
                    {FEATURES.map((feature, key) => {
                      const isActive = iconNum === feature.id;
                      const iconColor = isActive ? '#8959E5' : '#7a7a7a';
                      const featureIcon = cloneElement(feature.icon, { color: iconColor });
                      return (
                        <div
                          key={`${key}__${feature.id}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleFeatureSelect(feature.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleFeatureSelect(feature.id);
                            }
                          }}
                          css={css`
                            display: flex;
                            padding: 8px 12px;
                            align-items: flex-start;
                            gap: 10px;
                            align-self: stretch;
                            border-radius: 8px;
                            cursor: pointer;
                            border-left: 1px solid ${isActive ? '#8959E5' : 'transparent'};
                            background: ${isActive ? '#f4f1ff' : 'transparent'};
                            transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease;
                            outline: none;

                            &:focus-visible {
                              box-shadow: 0 0 0 2px rgba(137, 89, 229, 0.25);
                            }

                            &:hover {
                              background: #f4f1ff;
                            }
                          `}
                        >
                          <div
                            css={css`
                              width: 24px;
                              height: 24px;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              transition: color 0.25s ease;
                            `}
                          >
                            {featureIcon}
                          </div>
                          <div
                            css={css`
                              display: flex;
                              flex-direction: column;
                              align-items: flex-start;
                              gap: 4px;
                              flex: 1 0 0;
                            `}
                          >
                            <div
                              css={css`
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                align-self: stretch;
                              `}
                            >
                              <QSTypography
                                component={'p'}
                                css={css`
                                  align-self: stretch;
                                  color: var(--Neutral-Primary, #2d2d2d);
                                  font-family: Inter;
                                  font-size: 16px;
                                  font-style: normal;
                                  font-weight: 600;
                                  line-height: 140%;
                                `}
                              >
                                {feature?.title}
                              </QSTypography>
                            </div>
                            <QSTypography
                              css={css`
                                align-self: stretch;
                                color: #7a7a7a;
                                font-family: Inter;
                                font-size: 14px;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 140%;
                              `}
                            >
                              {feature?.description}
                            </QSTypography>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: flex-start;
              gap: 12px;
              align-self: stretch;
            `}
          >
            <QSButton
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;
                flex: 1 0 0;
                height: 40px;
              `}
              onClick={onClickContactUs}
            >
              {hideDTDetails ? 'Contact Us' : 'Contact Sales'}
            </QSButton>
          </div>
        </div>

        <div
          css={css`
            display: flex;
            width: 400px;
            flex-direction: column;
            background: #f5f0e6;
            border-radius: 8px;
          `}
        >
          <ProgressiveImage
            key={iconNum}
            imgProps={{
              css: {
                borderRadius: '0 8px 8px 0',
                transition: 'opacity 0.3s ease',
              },
            }}
            src={AIBlockerThumbnails[iconNum] ?? AIBlockerThumbnails[0]}
          />
        </div>
      </div>
    </QSModal>
  );
}
